import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'

import { classNames } from '../../helpers'

// info: blauer Hinweis (Standard). neutral: flache graue Leiste mit Rahmen
// für dauerhafte Hinweise, die nicht wie eine Meldung wirken sollen
// (Beispieldaten). Ab md sitzen Icon, Text, Aktion und X auf einer Mittellinie,
// darunter bleiben Icon und X auf Höhe der ersten Textzeile.
// warning und critical sind die Töne der Nutzungsbanner, gelb ab 80 % und rot
// ab voll. Sie haben das Layout von neutral und ein eigenes Standard-Icon.
const TONES = {
  info: {
    container: 'rounded-md bg-blue-50 p-4',
    layout: 'flex items-center',
    row: 'ml-3 flex-1 md:flex md:justify-between',
    text: 'text-balance text-sm text-primaryBlue',
    action: 'mt-3 text-sm md:ml-6 md:mt-0',
    icon: 'size-5 text-primaryBlue',
    dismiss: 'text-primaryBlue group-hover:text-primaryBlue/80',
    dismissHover: 'hover:bg-blue-100',
    focus: 'focus-visible:outline-primaryBlue'
  },
  neutral: {
    container:
      'rounded-md bg-gray-50 px-4 py-2.5 ring-1 ring-inset ring-gray-200',
    layout: 'flex items-start md:items-center',
    row: 'ml-3 min-w-0 flex-1 md:flex md:items-center md:justify-between',
    text: 'text-balance text-sm text-gray-700',
    action: 'mt-1.5 text-sm md:ml-6 md:mt-0 md:shrink-0',
    icon: 'size-5 text-gray-400',
    // gray-500 statt gray-400, damit das X 3:1 Kontrast zu gray-50 erreicht
    dismiss: 'text-gray-500 group-hover:text-gray-700',
    dismissHover: 'hover:bg-gray-200/60',
    focus: 'focus-visible:outline-gray-500'
  },
  warning: {
    container: 'rounded-md bg-yellow-50 p-4 ring-1 ring-inset ring-yellow-200',
    layout: 'flex items-start md:items-center',
    row: 'ml-3 min-w-0 flex-1 md:flex md:items-center md:justify-between',
    text: 'text-balance text-sm text-yellow-800',
    action: 'mt-3 text-sm md:ml-6 md:mt-0 md:shrink-0',
    icon: 'size-5 text-yellow-600',
    dismiss: 'text-yellow-700 group-hover:text-yellow-900',
    dismissHover: 'hover:bg-yellow-100',
    focus: 'focus-visible:outline-yellow-700',
    defaultIcon: ExclamationTriangleIcon
  },
  critical: {
    container: 'rounded-md bg-red-50 p-4 ring-1 ring-inset ring-red-200',
    layout: 'flex items-start md:items-center',
    row: 'ml-3 min-w-0 flex-1 md:flex md:items-center md:justify-between',
    text: 'text-balance text-sm text-red-800',
    action: 'mt-3 text-sm md:ml-6 md:mt-0 md:shrink-0',
    icon: 'size-5 text-red-500',
    dismiss: 'text-red-700 group-hover:text-red-900',
    dismissHover: 'hover:bg-red-100',
    focus: 'focus-visible:outline-red-700',
    defaultIcon: ExclamationCircleIcon
  }
}

/**
 * Info-Hinweis. Eine Farbe pro Ton statt der bisherigen Mischung aus
 * blue-400/blue-700/primaryBlue, und ein echter <button> fuer das Schliessen
 * statt eines <div onClick>.
 */
const InfoCallout = ({
  children,
  action,
  onDismiss,
  dismissLabel,
  tone,
  icon,
  className
}) => {
  const styles = TONES[tone] || TONES.info
  // Ohne icon gilt das Icon des Tons, info und neutral bleiben beim
  // InformationCircleIcon wie bisher
  const Icon = icon || styles.defaultIcon || InformationCircleIcon

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.layout}>
        <div className='flex flex-shrink-0'>
          <Icon aria-hidden='true' className={styles.icon} />
        </div>
        <div className={styles.row}>
          <div className={styles.text}>{children}</div>
          {action && <div className={styles.action}>{action}</div>}
        </div>
        {onDismiss && (
          <button
            type='button'
            onClick={onDismiss}
            aria-label={dismissLabel}
            title={dismissLabel}
            // p-1.5 und negativer Margin geben 32px Trefferfläche, das X
            // bleibt optisch an seinem Platz und die Zeile wird nicht höher
            className={classNames(
              'group -my-1.5 -mr-1.5 ml-1.5 flex-shrink-0 rounded-full p-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              styles.dismissHover,
              styles.focus
            )}
          >
            <XMarkIcon
              aria-hidden='true'
              className={classNames('size-5', styles.dismiss)}
            />
          </button>
        )}
      </div>
    </div>
  )
}

InfoCallout.propTypes = {
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  onDismiss: PropTypes.func,
  dismissLabel: PropTypes.string,
  tone: PropTypes.oneOf(['info', 'neutral', 'warning', 'critical']),
  icon: PropTypes.elementType,
  className: PropTypes.string
}

InfoCallout.defaultProps = {
  action: undefined,
  onDismiss: undefined,
  dismissLabel: undefined,
  tone: 'info',
  icon: undefined,
  className: undefined
}

export default InfoCallout
